import {
  isGameId,
  type GameCapabilities,
  type GameCapability,
  type GameDefinition,
  type GameFinishDefinition,
  type GameManifest,
  type GameId,
  type GamePricingVariant,
} from "modules/common/model/models";
import { normalizeGameFacetDefinitions } from "modules/common/cardFacets";

function normalizeCapabilities(value: unknown): GameCapabilities | null {
  if (!value || typeof value !== "object") return null;
  const capabilities = value as Partial<GameCapabilities>;
  if (
    typeof capabilities.catalogue !== "boolean" ||
    typeof capabilities.trading !== "boolean" ||
    typeof capabilities.onboarding !== "boolean" ||
    typeof capabilities.deckBuilder !== "boolean" ||
    typeof capabilities.legalities !== "boolean" ||
    typeof capabilities.scanner !== "boolean"
  ) {
    return null;
  }
  return {
    catalogue: capabilities.catalogue,
    trading: capabilities.trading,
    onboarding: capabilities.onboarding,
    deckBuilder: capabilities.deckBuilder,
    legalities: capabilities.legalities,
    scanner: capabilities.scanner,
  };
}

const KNOWN_PRICING_VARIANTS = new Set<GamePricingVariant>([
  "cardmarket-eur",
  "tcgplayer-usd",
]);

function normalizePricingVariants(value: unknown): GamePricingVariant[] | null {
  if (!Array.isArray(value)) return null;
  const variants = new Set<GamePricingVariant>();
  for (const candidate of value) {
    if (typeof candidate !== "string") return null;
    if (KNOWN_PRICING_VARIANTS.has(candidate as GamePricingVariant)) {
      variants.add(candidate as GamePricingVariant);
    }
  }
  return [...variants];
}

const FINISH_PRICE_KEYS = new Set([
  "nonfoil",
  "foil",
  "metal",
  "metalDeluxe",
  "etched",
]);
const FINISH_TIMESTAMP_KEYS = new Set([
  "nonfoilTimestamp",
  "foilTimestamp",
  "metalTimestamp",
  "metalDeluxeTimestamp",
  "etchedTimestamp",
]);
function normalizeFinish(value: unknown): GameFinishDefinition | null {
  if (!value || typeof value !== "object") return null;
  const finish = value as Partial<GameFinishDefinition>;
  if (
    typeof finish.value !== "string" ||
    !finish.value.trim() ||
    finish.value.length > 64 ||
    typeof finish.label !== "string" ||
    !finish.label.trim() ||
    typeof finish.order !== "number" ||
    !Number.isSafeInteger(finish.order) ||
    finish.order < 0 ||
    typeof finish.surface !== "boolean" ||
    typeof finish.foilVisual !== "boolean" ||
    (finish.priceKey !== undefined &&
      !FINISH_PRICE_KEYS.has(finish.priceKey)) ||
    (finish.timestampKey !== undefined &&
      !FINISH_TIMESTAMP_KEYS.has(finish.timestampKey))
  ) {
    return null;
  }
  return { ...finish } as GameFinishDefinition;
}

function normalizeFinishes(value: unknown): GameFinishDefinition[] | null {
  if (!Array.isArray(value)) return null;
  const finishes = new Map<string, GameFinishDefinition>();
  for (const candidate of value) {
    const finish = normalizeFinish(candidate);
    if (!finish || finishes.has(finish.value)) return null;
    finishes.set(finish.value, finish);
  }
  return finishes.size > 0
    ? [...finishes.values()].sort((left, right) => left.order - right.order)
    : null;
}

// Every valid server game remains available to generic product surfaces.
// Presentation metadata is optional and resolved through a generic fallback.
export function normalizeGameDefinitions(
  response: unknown
): GameDefinition[] | null {
  return normalizeGameManifest(response)?.games ?? null;
}

export function normalizeGameManifest(response: unknown): GameManifest | null {
  if (!response || typeof response !== "object") return null;
  const games = (response as Partial<GameManifest>).games;
  if (!Array.isArray(games)) return null;

  const normalized: GameDefinition[] = [];
  for (const candidate of games) {
    if (!candidate || typeof candidate !== "object") return null;
    const id = (candidate as Partial<GameDefinition>).id;
    const capabilities = normalizeCapabilities(
      (candidate as Partial<GameDefinition>).capabilities
    );
    if (!isGameId(id) || !capabilities) return null;
    const rawPricingVariants = (candidate as Partial<GameDefinition>)
      .pricingVariants;
    const pricingVariants = normalizePricingVariants(rawPricingVariants);
    if (!pricingVariants) return null;
    const rawFacets = (candidate as Partial<GameDefinition>).facets;
    const facets = normalizeGameFacetDefinitions(rawFacets);
    if (!facets) return null;
    const rawFinishes = (candidate as Partial<GameDefinition>).finishes;
    const finishes = normalizeFinishes(rawFinishes);
    if (!finishes || finishes.length === 0) return null;
    normalized.push({ id, capabilities, pricingVariants, facets, finishes });
  }
  const rawSharedFacets = (response as Partial<GameManifest>).sharedFacets;
  const sharedFacets = normalizeGameFacetDefinitions(rawSharedFacets);
  if (!sharedFacets) return null;
  return { games: normalized, sharedFacets };
}

export function gamesForCapability(
  capability: GameCapability,
  definitions: readonly GameDefinition[]
): GameId[] {
  return definitions
    .filter((definition) => definition.capabilities[capability])
    .map((definition) => definition.id);
}

export function gamesForPricingVariant(
  variant: GamePricingVariant,
  definitions: readonly GameDefinition[]
): GameId[] {
  return definitions
    .filter((definition) => definition.pricingVariants.includes(variant))
    .map((definition) => definition.id);
}
